import { useState, useEffect, useCallback } from 'react';
import api from '../../lib/api';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Wallet, RefreshCw, Loader2, CheckCircle2, AlertTriangle, Clock } from 'lucide-react';

const fmtTs = (v) => {
  if (!v) return '—';
  const d = new Date(v);
  return isNaN(d) ? v : d.toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' });
};

const STATUS = {
  ready: { cls: 'bg-emerald-100 text-emerald-700', icon: CheckCircle2 },
  building: { cls: 'bg-sky-100 text-sky-700', icon: Loader2 },
  queued: { cls: 'bg-slate-100 text-slate-600', icon: Clock },
  failed: { cls: 'bg-red-100 text-red-700', icon: AlertTriangle },
};

export default function BudgetBuildsJobPanel() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/procurement/admin/budget-builds', { params: { limit: 25 } });
      setData(data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const stats = data?.stats || {};
  const jobs = data?.jobs || [];

  return (
    <Card className="p-5 mb-6" data-testid="cockpit-budget-builds-job">
      <div className="flex items-start justify-between flex-wrap gap-3 mb-3">
        <div className="flex items-start gap-3">
          <div className="h-10 w-10 rounded-lg bg-violet-500/10 flex items-center justify-center">
            <Wallet className="h-5 w-5 text-violet-600" />
          </div>
          <div>
            <div className="font-semibold flex items-center gap-2">
              Budget Builds <Badge variant="secondary">Async</Badge>
            </div>
            <div className="text-sm text-muted-foreground mt-1">
              Baseline build jobs across all orgs ·{' '}
              <span className="text-emerald-600 font-medium">{stats.ready ?? 0} ready</span>{' · '}
              <span className="text-sky-600 font-medium">{stats.building ?? 0} building</span>{' · '}
              <span className="text-red-600 font-medium">{stats.failed ?? 0} failed</span>
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={load} data-testid="cockpit-builds-refresh">
          {loading ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-1" />} Refresh
        </Button>
      </div>

      {jobs.length === 0 ? (
        <div className="text-sm text-muted-foreground py-4">No budget builds recorded yet.</div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-xs uppercase tracking-wider text-muted-foreground border-b">
                <th className="text-left py-2">Budget</th>
                <th className="text-left">Status</th>
                <th className="text-left">By</th>
                <th className="text-right">Scopes</th>
                <th className="text-right">Duration</th>
                <th className="text-right">Created</th>
              </tr>
            </thead>
            <tbody>
              {jobs.map((j) => {
                const s = STATUS[j.status] || STATUS.queued;
                const Icon = s.icon;
                return (
                  <tr key={j.id} className="border-b last:border-0" data-testid={`build-job-${j.id}`}>
                    <td className="py-2 font-medium max-w-[220px] truncate" title={j.budget_name}>{j.budget_name || '—'}</td>
                    <td>
                      <Badge className={`${s.cls} text-[11px] capitalize`}>
                        <Icon className={`h-3 w-3 mr-1 ${j.status === 'building' ? 'animate-spin' : ''}`} />{j.status}
                      </Badge>
                      {j.status === 'failed' && j.error && (
                        <div className="text-[11px] text-red-600 mt-0.5 max-w-[260px] truncate" title={j.error}>{j.error}</div>
                      )}
                    </td>
                    <td className="text-muted-foreground">{j.triggered_by || '—'}</td>
                    <td className="text-right">{j.scope_count ?? '—'}</td>
                    <td className="text-right">{j.duration_ms != null ? `${(j.duration_ms / 1000).toFixed(2)}s` : '—'}</td>
                    <td className="text-right text-muted-foreground">{fmtTs(j.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
